import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartCard } from "./chart-card";
import { Button } from "@/components/ui/button";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { Trophy, Award, Users, Download, X, Check } from "lucide-react";
import { useAuth } from "@/global-hooks/useAuth";
import { downloadChartPng } from "./utils";
import { NO_ANIMATION } from "./chart-ticks";
import { toAPIFilter } from "./utils";
import type { StatisticsFilters } from "./StatisticsControls";
import statisticsAPIService, { type AwardData } from "@/lib/statistics-api.service";
import { ModalShell } from "@/components/ui/modal-shell";

interface AwardStatisticsTabProps {
	/** Bộ lọc của cả trang (khoa, khoá, khoảng ngày). Thiếu nó thì mục này
	 * hiện số của mọi khoá ngay cạnh các tab đã lọc. */
	filters?: StatisticsFilters;
	/** Tăng lên mỗi lần trang yêu cầu tải lại; đổi giá trị là fetch lại. */
	refreshToken?: number;
}

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D"];

export default function AwardStatisticsTab({ filters, refreshToken = 0 }: AwardStatisticsTabProps) {
	const awardRef = useRef<HTMLDivElement>(null);
	const { user } = useAuth();
	const [awardStats, setAwardStats] = useState<AwardData[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedAward, setSelectedAward] = useState<AwardData | null>(null);

	const canExport = user?.role === "admin" || user?.role === "manager";

	// Tách khỏi JSX để hàng nút của modal đủ gọn mà đặt vào footer cố định.
	const exportSelectedAwardCsv = () => {
		if (!selectedAward) return;

		const csvContent = [
			["STT", "Mã sinh viên", "Họ và tên", "Chi tiết giải"].join(";"),
			...selectedAward.students.map((student, index) =>
				[index + 1, student.studentId, `"${student.name}"`, `"${student.awardDetails}"`].join(";"),
			),
		].join("\n");

		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute(
			"download",
			`danh-sach-${selectedAward.awardType.replace(/\s+/g, "-").toLowerCase()}.csv`,
		);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	useEffect(() => {
		const fetchAwardStats = async () => {
			try {
				setIsLoading(true);
				const data = await statisticsAPIService.getAwardStatistics(toAPIFilter(filters, "total"));
				setAwardStats(data.sort((a, b) => a.code.localeCompare(b.code)));
			} catch (err) {
				console.error("Error fetching award statistics:", err);
				setError(err instanceof Error ? err.message : "Failed to load award statistics");
			} finally {
				setIsLoading(false);
			}
		};

		fetchAwardStats();
		// `refreshToken`: nút Tải lại của trang phải làm mới cả mục này.
	}, [refreshToken, filters]);

	const totalWithAwards = awardStats.reduce((sum, item) => sum + item.total, 0);
	const mostCommonAward =
		awardStats.length > 0
			? awardStats.reduce((max, item) => (item.total > max.total ? item : max))
			: null;

	const pieData = awardStats.map((item) => ({
		name: item.awardType,
		value: item.total,
		percentage: item.percentage,
	}));

	// Chỉ chiếm chỗ khi chưa có gì để hiện. Lần tải lại sau đó giữ nguyên biểu đồ
	// cũ thay vì thay nó bằng một dòng chữ rồi dựng lại từ đầu.
	if (isLoading && awardStats.length === 0) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-center p-8">
					<div className="text-center">
						<div className="animate-pulse text-lg">Đang tải thống kê giải thưởng...</div>
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-center p-8">
					<div className="text-center text-red-600 dark:text-red-400">
						<div className="text-lg font-medium">Lỗi tải dữ liệu</div>
						<div className="text-sm">{error}</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div ref={awardRef}>
				<div className="flex items-center justify-between mb-6">
					<div className="flex items-center gap-3">
						<Trophy className="h-6 w-6 text-yellow-700 dark:text-yellow-400" />
						<div>
							<h2 className="text-2xl font-bold">Thống kê Giải thưởng</h2>
							<p className="text-muted-foreground">Phân tích giải thưởng của sinh viên</p>
						</div>
					</div>

					<div className="flex items-center gap-2">
						{canExport && (
							<Button
								onClick={() => {
									const csvContent = [
										["Loại giải thưởng", "Số lượng", "Tỷ lệ (%)"].join(";"),
										...awardStats.map((item) =>
											[`"${item.awardType}"`, item.total, item.percentage.toFixed(2)].join(";"),
										),
									].join("\n");

									const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
									const link = document.createElement("a");
									const url = URL.createObjectURL(blob);
									link.setAttribute("href", url);
									link.setAttribute("download", "thong-ke-giai-thuong.csv");
									link.style.visibility = "hidden";
									document.body.appendChild(link);
									link.click();
									document.body.removeChild(link);
								}}
								variant="outline"
								size="sm"
								className="flex items-center gap-2"
							>
								<Download className="h-4 w-4" />
								Tải CSV
							</Button>
						)}
						<Button
							onClick={() => downloadChartPng(awardRef, "thong-ke-giai-thuong")}
							variant="outline"
							size="sm"
							className="flex items-center gap-2"
						>
							<Download className="h-4 w-4" />
							Tải PNG
						</Button>
					</div>
				</div>

				{/* Summary Cards */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Tổng SV có giải</CardTitle>
							<Users className="h-4 w-4 text-yellow-700 dark:text-yellow-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
								{totalWithAwards.toLocaleString()}
							</div>
							<p className="text-xs text-muted-foreground">Sinh viên đạt giải thưởng</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Loại giải phổ biến</CardTitle>
							<Award className="h-4 w-4 text-purple-600 dark:text-purple-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
								{mostCommonAward ? mostCommonAward.awardType : "N/A"}
							</div>
							<p className="text-xs text-muted-foreground">
								{mostCommonAward ? `${mostCommonAward.total} sinh viên` : ""}
							</p>
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium">Số loại giải</CardTitle>
							<Trophy className="h-4 w-4 text-green-700 dark:text-green-400" />
						</CardHeader>
						<CardContent>
							<div className="text-2xl font-bold text-green-700 dark:text-green-400">
								{awardStats.length}
							</div>
							<p className="text-xs text-muted-foreground">Danh mục giải thưởng</p>
						</CardContent>
					</Card>
				</div>

				{/* Charts */}
				<div className="mb-6">
					{/* Pie Chart */}
					<ChartCard
						title="Tỷ lệ loại giải thưởng"
						filename="ty-le-loai-giai-thuong"
						icon={<PieChart className="h-5 w-5" />}
					>
						<div className="h-96">
							<ResponsiveContainer width="100%" height="100%">
								<PieChart>
									<Pie
										{...NO_ANIMATION}
										data={pieData}
										cx="50%"
										cy="50%"
										labelLine={false}
										label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
										outerRadius={120}
										fill="#8884d8"
										dataKey="value"
									>
										{pieData.map((_, index) => (
											<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
										))}
									</Pie>
									<Tooltip
										formatter={(value: number, _name: string, props: any) => [
											value.toLocaleString(),
											`${props.payload.name} - Số lượng`,
										]}
									/>
									<Legend />
								</PieChart>
							</ResponsiveContainer>
						</div>
					</ChartCard>
				</div>

				{/* Detailed Table */}
				<Card>
					<CardHeader>
						<CardTitle>Chi tiết thống kê giải thưởng</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="overflow-x-auto">
							<table className="w-full border-collapse">
								<thead>
									<tr className="border-b">
										<th className="text-left p-3 font-medium">Loại giải thưởng</th>
										<th className="text-right p-3 font-medium">Số lượng</th>
										<th className="text-right p-3 font-medium">Tỷ lệ</th>
										<th className="text-center p-3 font-medium">Chi tiết</th>
									</tr>
								</thead>
								<tbody>
									{awardStats.map((item, index) => (
										<tr key={index} className="border-b hover:bg-accent">
											<td className="p-3 font-medium">{item.awardType}</td>
											<td className="p-3 text-right text-yellow-700 dark:text-yellow-400 font-bold">
												{item.total.toLocaleString()}
											</td>
											<td className="p-3 text-right">{item.percentage.toFixed(2)}%</td>
											<td className="p-3 text-center">
												<Button
													variant="outline"
													size="sm"
													onClick={(e) => {
														e.stopPropagation();
														setSelectedAward(item);
													}}
												>
													Xem {item.students.length} sinh viên
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Student Details Modal */}
			{selectedAward && (
				<ModalShell
					isOpen
					onClose={() => setSelectedAward(null)}
					title={`Danh sách sinh viên đạt giải: ${selectedAward.awardType}`}
					size="4xl"
					closeButton="labeled"
					footer={
						<div className="flex justify-end gap-2">
							<Button
								onClick={exportSelectedAwardCsv}
								variant="outline"
								size="sm"
								className="flex items-center gap-2"
							>
								<Download className="h-4 w-4" />
								Tải CSV
							</Button>
							<Button onClick={() => setSelectedAward(null)} size="sm">
								Đóng
							</Button>
						</div>
					}
				>
					<div className="mb-4 text-sm text-muted-foreground">
						Tổng cộng: {selectedAward.students.length} sinh viên (
						{selectedAward.percentage.toFixed(2)}%)
					</div>

					{/* Không bọc `overflow-x-auto` quanh bảng: CSS nâng trục Y của phần tử
					    đó lên `auto` theo, biến nó thành vùng cuộn riêng và `sticky` sẽ
					    bám vào nó thay vì vào thân modal — hàng tiêu đề mất tác dụng khi
					    danh sách dài. Để thân ModalShell cuộn cả hai trục, `min-w` lo phần
					    ngang cho 5 cột chữ. */}
					<table className="w-full min-w-[40rem] border-collapse">
						<thead className="sticky top-0 bg-muted">
							<tr className="border-b">
								<th className="text-left p-3 font-medium">STT</th>
								<th className="text-left p-3 font-medium">Mã sinh viên</th>
								<th className="text-left p-3 font-medium">Họ và tên</th>
								<th className="text-left p-3 font-medium">Chi tiết giải</th>
								<th className="text-left p-3 font-medium">Tiếp nhận</th>
							</tr>
						</thead>
						<tbody>
							{selectedAward.students.map((student, index) => (
								<tr key={student.studentId} className="border-b hover:bg-accent">
									<td className="p-3">{index + 1}</td>
									<td className="p-3 font-mono">{student.studentId}</td>
									<td className="p-3 font-medium">{student.name}</td>
									<td className="p-3">{student.awardDetails}</td>
									<td className="p-3">
										{student.daTiepNhan ? (
											<Check className="h-4 w-4 text-green-700 dark:text-green-400" />
										) : (
											<X className="h-4 w-4 text-red-600 dark:text-red-400" />
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</ModalShell>
			)}
		</div>
	);
}
